import { TrendingUp, Lightbulb, Target } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";

interface GrowthPotentialProps {
  potential?: {
    areas: string[];
    recommendations: string[];
  } | string | null;
}

export function GrowthPotential({ potential }: GrowthPotentialProps) {
  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('Growth potential raw data:', potential);
    console.log('Growth potential type:', typeof potential);
  }

  // Handle string format (in case it comes as JSON string)
  let parsedPotential: { areas: string[]; recommendations: string[] } | null = null;
  
  if (typeof potential === 'string') {
    try {
      parsedPotential = JSON.parse(potential);
      if (process.env.NODE_ENV === 'development') {
        console.log('Parsed growth potential from string:', parsedPotential);
      }
    } catch (e) {
      console.error('Failed to parse growth potential:', e);
      return (
        <div className="text-sm text-muted-foreground italic">
          <p>Growth potential data format error.</p>
          {process.env.NODE_ENV === 'development' && (
            <p className="text-xs mt-1 text-red-500">Raw data: {potential}</p>
          )}
        </div>
      );
    }
  } else if (potential && typeof potential === 'object') {
    parsedPotential = potential;
    if (process.env.NODE_ENV === 'development') {
      console.log('Growth potential object data:', parsedPotential);
    }
  }

  if (!parsedPotential || (!parsedPotential.areas?.length && !parsedPotential.recommendations?.length)) {
    return (
      <div className="text-sm text-muted-foreground italic">
        <p>Growth potential analysis not available.</p>
        {process.env.NODE_ENV === 'development' && (
          <div className="text-xs mt-2 p-2 bg-gray-100 rounded">
            <p>Debug info:</p>
            <p>Areas: {parsedPotential?.areas?.length || 0}</p>
            <p>Recommendations: {parsedPotential?.recommendations?.length || 0}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {parsedPotential.areas?.length > 0 && (
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center">
              <TrendingUp className="h-4 w-4 mr-2 text-green-600 flex-shrink-0" />
              Growth Areas
            </h4>
            <div className="space-y-2">
              {parsedPotential.areas.map((area, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Target className="h-3 w-3 text-green-600 mt-1 flex-shrink-0" />
                  <p className="text-sm text-green-700 leading-relaxed">
                    {area}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {parsedPotential.recommendations?.length > 0 && (
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center">
              <Lightbulb className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
              Development Recommendations
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full ml-2">
                Action Items
              </span>
            </h4>
            <div className="space-y-3">
              {parsedPotential.recommendations.map((rec, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-blue-100">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-blue-600">{index + 1}</span>
                  </div>
                  <p className="text-sm text-blue-700 leading-relaxed font-medium">
                    {rec}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-100">
              💡 These recommendations provide actionable steps for candidate development
            </div>
          </CardContent>
        </Card>
      )}


    </div>
  );
}